// The "Which surgery?" list, from the surgery catalogue (GET /public/surgeries) for the
// body area already chosen: the procedures switched on for patients in that area, by
// name, then the area's catch-all row ("Other knee surgery", else "Other surgery"),
// which is what "Another surgery" records. Every value sent back is a catalogue name
// exactly, because coverage and enrolment match on it.
//
// If the request fails, or has not answered within a few seconds, the question still
// works from a small built-in list: the knee and hip replacements the funnel always
// listed, for those two areas only, and the catch-all rows, which have been in the
// catalogue since it had "Other" rows. A real list that arrives later replaces it, unless
// the visitor has already started answering on it.
//
// Nothing here reaches analytics: the area goes to our own API, the answer to
// /public/intake only.
import { useEffect, useRef, useState } from 'react';
import { getSurgeries, type SurgeryList } from './api';

const GENERAL_OTHER = 'Other surgery';
const FALLBACK_AFTER_MS = 5000; // show the built-in list
const GIVE_UP_AFTER_MS = 15000; // abort the request, so a later visit asks again

const BUILT_IN: Record<string, SurgeryList> = {
  Knee: {
    bodyRegionSlug: 'knee',
    surgeries: [
      { name: 'Partial Knee Replacement', bodyRegionSlug: 'knee' },
      { name: 'Total Knee Replacement', bodyRegionSlug: 'knee' },
    ],
    other: { name: 'Other knee surgery', bodyRegionSlug: 'knee' },
  },
  Hip: {
    bodyRegionSlug: 'hip',
    surgeries: [
      { name: 'Anterior Total Hip Replacement', bodyRegionSlug: 'hip' },
      { name: 'Partial Hip Replacement', bodyRegionSlug: 'hip' },
      { name: 'Posterior Total Hip Replacement', bodyRegionSlug: 'hip' },
    ],
    other: { name: 'Other hip surgery', bodyRegionSlug: 'hip' },
  },
};

export function builtInSurgeries(bodyPart?: string): SurgeryList {
  return (
    (bodyPart && BUILT_IN[bodyPart]) || {
      bodyRegionSlug: null,
      surgeries: [],
      other: { name: GENERAL_OTHER, bodyRegionSlug: null },
    }
  );
}

// What "Another surgery" records: the area's catch-all row.
export function catchAllName(list: SurgeryList): string {
  return list.other?.name || GENERAL_OTHER;
}

// One request per area per page load. A failed or abandoned one is forgotten, so a later
// visit asks again.
const cache = new Map<string, Promise<SurgeryList>>();

function load(bodyPart?: string, detail?: string): Promise<SurgeryList> {
  const key = `${bodyPart ?? ''}|${detail ?? ''}`;
  let pending = cache.get(key);
  if (!pending) {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const giveUp = window.setTimeout(() => controller?.abort(), GIVE_UP_AFTER_MS);
    pending = getSurgeries({ bodyPart, detail }, controller ? { signal: controller.signal } : undefined)
      .then((list) => {
        if (!list || !Array.isArray(list.surgeries)) throw new Error('bad_response');
        return list;
      })
      .finally(() => window.clearTimeout(giveUp));
    pending.catch(() => cache.delete(key));
    cache.set(key, pending);
  }
  return pending;
}

export interface SurgeryListState {
  list: SurgeryList | null; // null while loading
  builtIn: boolean; // the built-in list is showing
  hold: () => void; // the visitor started answering: keep the list they see
}

export function useSurgeryList(bodyPart?: string, detail?: string): SurgeryListState {
  const [state, setState] = useState<{ list: SurgeryList | null; builtIn: boolean }>({ list: null, builtIn: false });
  const held = useRef(false);
  useEffect(() => {
    let live = true;
    held.current = false;
    setState({ list: null, builtIn: false });
    const showBuiltIn = () => {
      if (live) setState((s) => (s.list ? s : { list: builtInSurgeries(bodyPart), builtIn: true }));
    };
    const timer = window.setTimeout(showBuiltIn, FALLBACK_AFTER_MS);
    load(bodyPart, detail)
      .then((list) => {
        // A list that swapped under a finger would invite the wrong tap, so once the
        // visitor has started answering on the built-in list, it stays.
        if (live) setState((s) => (s.builtIn && held.current ? s : { list, builtIn: false }));
      })
      .catch(showBuiltIn)
      .finally(() => window.clearTimeout(timer));
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [bodyPart, detail]);
  return {
    ...state,
    hold: () => {
      held.current = true;
    },
  };
}
